import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  /** Stored in minutes; null when the stop has no length. */
  value: number | null;
  onChange: (mins: number | null) => void;
  label: string;
}

function split(mins: number | null) {
  return mins == null ? { h: '', m: '' } : { h: mins >= 60 ? String(Math.floor(mins / 60)) : '', m: String(mins % 60) };
}

function total(h: string, m: string): number | null {
  if (!h && !m) return null;
  return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
}

/** Hours and minutes side by side: "117" read as a length nobody thinks in. */
export default function DurationInput({ value, onChange, label }: Props) {
  const { t } = useTranslation();
  const [parts, setParts] = useState(() => split(value));
  const [seenValue, setSeenValue] = useState(value);

  // A value that is not what these two boxes add up to came from outside (the
  // form loading a stop, or resetting): show it. Otherwise leave the boxes as
  // typed, so "90" in minutes is not rewritten to 1 h 30 mid-edit. Synced
  // during render, not in an effect: an effect left the old hours on screen
  // for one more commit after the form reset.
  if (value !== seenValue) {
    setSeenValue(value);
    if (total(parts.h, parts.m) !== value) setParts(split(value));
  }

  function set(next: { h: string; m: string }) {
    setParts(next);
    onChange(total(next.h, next.m));
  }

  const digits = (v: string) => v.replace(/\D/g, '').slice(0, 3);

  return (
    <div className="flex items-center gap-2">
      <input
        type="text" inputMode="numeric" className="input w-16 text-center" placeholder="0"
        aria-label={`${label} (${t('itinerary.form.durationHours')})`}
        value={parts.h} onChange={e => set({ ...parts, h: digits(e.target.value) })}
      />
      <span className="text-sm text-slate-500 dark:text-slate-400" aria-hidden>{t('itinerary.form.durationHours')}</span>
      <input
        type="text" inputMode="numeric" className="input w-16 text-center" placeholder="0"
        aria-label={`${label} (${t('itinerary.form.durationMinutes')})`}
        value={parts.m} onChange={e => set({ ...parts, m: digits(e.target.value) })}
      />
      <span className="text-sm text-slate-500 dark:text-slate-400" aria-hidden>{t('itinerary.form.durationMinutes')}</span>
    </div>
  );
}
